<template>
  <div class="h-full overflow-y-scroll pr-2 max-sm:pr-0 max-sm:[scrollbar-width:none]" ref="wrapperRef">
    <Pager
      :pageLoadRequest="loadNotifications"
      :count="20"
      category="notification"
    >
      <div class="flex justify-between gap-2 mb-2 max-sm:hidden">
        <div class="text-lg text-text font-medium">{{ $t('notifications.title') }}</div>
        <SimpleButton variant="ghost" style="padding: 0 8px" @click="markAllRead">
          {{ $t('notifications.mark_all_read') }}
        </SimpleButton>
      </div>
      <template v-slot:loader>
        {{$t('loader.Loading')}}...
      </template>
      <template v-for="group in groupedNotifications" :key="group.label">
        <div class="mt-3.5 mb-1.5 ml-0.5 text-label font-medium uppercase tracking-[.04em] text-text-muted">{{ group.label }}</div>
        <Notification
          :notification='n'
          v-for='n in group.items'
          :key='n.id'
          @navigate="emit('close')"
        />
      </template>
      <div v-if="!notifications.length" class="flex flex-col items-center gap-2 px-4 pt-[20vh] text-center">
        <IconBell class="size-7.5 text-text-muted" />
        <span class="text-body font-semibold text-text">{{ $t('notifications.all_caught_up') }}</span>
        <span class="text-label text-text-muted">{{ $t('notifications.all_caught_up_hint') }}</span>
      </div>
    </Pager>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import { onClickOutside } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import dayjs from 'dayjs';
import SimpleButton from './SimpleButton.vue';
import { IconBell } from '@iconify-prerendered/vue-heroicons';

const emit = defineEmits(['close']);

const store = useStore();
const { t } = useI18n();

// Template refs
const wrapperRef = ref(null);

// Click outside handler is intentionally disabled here.
// Closing is handled by the fixed glass overlay in CommonHeader so the bell button
// and the notification panel do not fight each other on click events.

// Computed properties
const user = computed(() => store.state.user.user);
const CLEARABLE_TYPES = ['assign', 'mention', 'dayoff', 'invitation', 'inviteReject', 'invoiceAutoGenFailed', 'invoiceAutoGenSent'];
const notifications = computed(() => store.state.user.notifications.filter(
  (n) => !(CLEARABLE_TYPES.includes(n.type) && n.deleted === 1),
));
const workspace = computed(() => store.state.user.workspace);

const dayLabel = (date) => {
  const day = dayjs(date).format('YYYY-MM-DD');
  if (day === dayjs().format('YYYY-MM-DD')) {
    return t('taskFilters.today');
  }
  if (day === dayjs().subtract(1, 'day').format('YYYY-MM-DD')) {
    return t('taskFilters.yesterday');
  }
  return dayjs(date).format('DD MMM');
};

const groupedNotifications = computed(() => {
  const groups = [];
  notifications.value.forEach((n) => {
    const label = dayLabel(n.createdAt);
    const lastGroup = groups[groups.length - 1];
    if (lastGroup && lastGroup.label === label) {
      lastGroup.items.push(n);
    } else {
      groups.push({ label, items: [n] });
    }
  });
  return groups;
});

// Methods
const clearNotificationsList = () => {
  store.commit('user/clearNotificationsList');
};

const actionGetNotificationPaged = (payload) => {
  return store.dispatch('user/actionGetNotificationPaged', payload);
};

const markAllRead = () => {
  store.dispatch('user/actionMarkAllNotificationsRead');
};

const actionCheckUnhandledNotifics = () => {
  return store.dispatch('user/actionCheckUnhandledNotifics');
};

const loadNotifications = async (category, offset, count, called, order, cb) => {
  const data = await actionGetNotificationPaged({
    offset,
    count,
    called,
    workspaceId: workspace.value.id,
  });
  cb(data);
};

// Lifecycle
onMounted(() => {
  clearNotificationsList(); // we reload them ever time
  
  // will "clean up" bell if user created request and then instantly delated it
  actionCheckUnhandledNotifics();
});
</script>
